import { createRouter, createWebHistory } from 'vue-router'
import { pinia, useStageStore } from '@/store'
import { startPageSession, endPageSession, setupPageTrackListeners } from '@/utils/pageTrack'

import Live from '@/views/live/index'
// import Star from '@/views/star/index'
import Ranking from '@/views/ranking/index'
import Store from '@/views/store/index'
import StoreTask from "@/views/store/task/index.vue";
import StoreMall from "@/views/store/mall/index.vue";
import StoreDedicate from "@/views/store/dedicate/index.vue";
// import Apply from '@/views/apply/index'
import Schedule from '@/views/schedule/index'
import Detail from '@/views/ranking/teams/detail'
import DetailClan from '@/views/ranking/teams/detail/clan'
import DetailMembers from '@/views/ranking/teams/detail/members'
import DetailFanRanking from '@/views/ranking/teams/detail/fan'

import Quiz from '@/views/quiz/index'

// 这些参数需要在站内跳转时继续保留：
// - stage/env: 调试阶段和测试环境标记
// - token/avatar/taptapavatar: 链接直达登录和头像初始化依赖
const PERSISTED_QUERY_KEYS = ['stage', 'env', 'token', 'avatar', 'taptapavatar']

// 路由 name → 埋点页面 name (数据同事约定 6 类)
// null 表示该路由不埋点 (StoreDedicate 累计人气贡献 tab)
const ROUTE_TO_PAGE_NAME = Object.freeze({
	Schedule: 'match_schedule',
	Live: 'live',
	Quiz: 'quiz',
	StoreTask: 'task',
	StoreMall: 'exchange',
	StoreDedicate: null,
	Ranking: 'ranking',
	Detail: 'ranking',
	DetailClan: 'ranking',
	DetailMembers: 'ranking',
	DetailFanRanking: 'ranking',
})

const STORE_TAB_ROUTE_MAP = Object.freeze({
	task: 'StoreTask',
	mall: 'StoreMall',
	dedicate: 'StoreDedicate'
})

const STORE_TAB_STORAGE_KEY = 'sausage-match:store-tab'

const getStoredStoreTab = () => {
	if (typeof window === 'undefined') {
		return ''
	}

	const storedTab = window.sessionStorage.getItem(STORE_TAB_STORAGE_KEY) || ''
	return STORE_TAB_ROUTE_MAP[storedTab] ? storedTab : ''
}

const mergePersistentQuery = (toQuery = {}, fromQuery = {}) => {
	const query = { ...toQuery }
	let changed = false

	PERSISTED_QUERY_KEYS.forEach((key) => {
		if (fromQuery[key] !== undefined && query[key] === undefined) {
			query[key] = fromQuery[key]
			changed = true
		}
	})

	return changed ? query : null
}

const router = createRouter({
	history: createWebHistory(),
	routes: [
		{
			path: '/',
			redirect: (to) => ({
				name: 'Live',
				query: to.query
			})
		},
		{
			path: '/store',
			name: 'Store',
			component: Store,
			redirect: () => ({
				name: STORE_TAB_ROUTE_MAP[getStoredStoreTab()] || 'StoreTask'
			}),
			children: [
				{
					path: 'task',
					name: 'StoreTask',
					component: StoreTask,
				},
				{
					path: 'mall',
					name: 'StoreMall',
					component: StoreMall,
				},
				{
					path: 'dedicate',
					name: 'StoreDedicate',
					component: StoreDedicate,
				},
			]
		},
		{
			path: '/schedule',
			name: 'Schedule',
			component: Schedule
		},
		{
			path: '/live',
			name: 'Live',
			component: Live
		},
		{
			path: '/ranking/:tab(popularity|personal|team)?',
			name: 'Ranking',
			component: Ranking
		},
		{
			path: '/teams/:id',
			name: 'Detail',
			component: Detail,
			redirect: (to) => ({
				name: 'DetailClan',
				params: to.params,
				query: to.query
			}),
			children: [
				{
					path: 'clan',
					name: 'DetailClan',
					component: DetailClan
				},
				{
					path: 'members',
					name: 'DetailMembers',
					component: DetailMembers
				},
				{
					path: 'fan',
					name: 'DetailFanRanking',
					component: DetailFanRanking
				}
			]
		},
		{
			path: '/quiz/:type(schedule|live|rank-pk|disuse-pk)?',
			name: 'Quiz',
			component: Quiz
		},
		// 兜底: 未匹配路径 (如老直链 /prize /teams /star) 统一回首页
		{
			path: '/:pathMatch(.*)*',
			redirect: '/'
		}
	]
})

// 首屏路由: 等 stage 就绪 (config 加载完成或失败) 再决定放行/重定向,
// 避免直达 /live 等未开放页签时 view 抢先 mount 发起一阶段 API 请求
router.beforeEach(async (to, from, next) => {
	const mergedQuery = mergePersistentQuery(to.query, from.query)
	if (mergedQuery) {
		next({
			path: to.path,
			query: mergedQuery,
			hash: to.hash,
			replace: true
		})
		return
	}

	const stageStore = useStageStore(pinia)
	await stageStore.whenReady()

	if (stageStore.scheduleOnly && to.name !== 'Schedule') {
		// 携带 query: 首次进站时 from 为空, 后续的 mergePersistentQuery 捡不回 token/stage
		next({ name: 'Schedule', query: to.query })
		return
	}

	next()
})

// 埋点: pv + page_stay
// 路由切换时上报旧 pageName 的 page_stay + 新 pageName 的 pv
// 同一 pageName 内的子路由切换不重复触发 (如 Ranking 内 Detail / DetailClan 切换)
setupPageTrackListeners()

router.afterEach((to, from) => {
	const toPage = ROUTE_TO_PAGE_NAME[to.name]
	const fromPage = ROUTE_TO_PAGE_NAME[from.name]
	if (toPage === fromPage) return
	if (fromPage) endPageSession()
	if (toPage) startPageSession(toPage)
})

export default router;
